import type { DeviceMemory } from '#src/groups/device_memory/device_memory.ts';
import { DeviceMemoryRules } from '#src/groups/device_memory/device_memory.ts';
import { useEffect, useState } from 'react';

const TICK_INTERVAL_MS = 250;

export interface NameChangeWait {
  isWaiting: boolean;
  /** When the wait ends, in epoch milliseconds. */
  endsAt: number;
}

/**
 * Whether `participantId` must wait before it can be confirmed on this device.
 *
 * The countdown starts when the confirmation mounts and is never stored, so leaving the
 * screen and coming back restarts it. This only re-renders when the wait ends, and reaching
 * the end never continues by itself. Use `useCountdown` for the ticking remaining time.
 */
export function useNameChangeWait(memory: DeviceMemory, participantId: string): NameChangeWait {
  const [openedAt] = useState(() => Date.now());
  const [now, setNow] = useState(openedAt);
  const remainingMs = DeviceMemoryRules.remainingWaitMs(memory, participantId, openedAt, now);
  const isWaiting = remainingMs > 0;

  useEffect(() => {
    if (!isWaiting) return;
    const timeout = setTimeout(() => setNow(Date.now()), remainingMs);
    return () => clearTimeout(timeout);
  }, [isWaiting, remainingMs]);

  return { isWaiting, endsAt: now + remainingMs };
}

/** Milliseconds left until `endsAt`, re-rendering a few times a second until it reaches zero. */
export function useCountdown(endsAt: number): number {
  const [now, setNow] = useState(() => Date.now());
  const remainingMs = Math.max(0, endsAt - now);
  const isCounting = remainingMs > 0;

  useEffect(() => {
    if (!isCounting) return;
    const interval = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isCounting]);

  return remainingMs;
}
